<script setup lang="ts">
import { inject, computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { LocaleCodeKey, defaultLocaleCode } from '../../../utils/i18n'
import MultiSelect from '@vueform/multiselect'
import _ from 'lodash'
import { get } from '@rails/request.js'

export type Company = {
  id: number | null,
  name: string
}

export type Option = {
  value: Company,
  label: string
}

const locale = inject(LocaleCodeKey)
const { t } = useI18n({ locale: locale || defaultLocaleCode })

const props = defineProps<{
  companies: Company[],
  formLabel: string,
  category: "publisher" | "developer"
}>()

const selectedCompanies = computed(() => {
  return props.companies.map((company: Company) => {
    return { value: company, label: company.name }
  })
})

const searchOptions = async(url: string, query: string): Promise<Option[] | null> => {
  const response = await get(url, {
    responseKind: "json",
    query: { name: query }
  })
  if(response.ok) {
    const json = await response.json
    return json.companies.map((company: Company) => {
      return { value: company, label: company.name }
    })
  }
  return null;
}

const autocomplete = async (query: string) => {
  return await searchOptions('/companies/autocomplete', query);
}

const emit = defineEmits(['update:companies'])

const selectCompany = (e: { originalEvent: Event, value: Company | string }) => {
  let company: Company

  if (typeof e.value == 'string') {
    company = {
        id: null,
        name: e.value
    }
  } else {
    company = e.value
  }
  emit('update:companies', [...selectedCompanies.value.map(sc=>sc.value), company])
}

const onClearCompany = () => {
  emit('update:companies', [])
}

const deselectCompany = (e: { originalEvent: Event, value: Company | string }) => {
  emit('update:companies', selectedCompanies.value.filter((option: Option) => {
    if (typeof e.value == 'string') {
      return option.value.id || option.label != e.value
    } else {
      return option.value.id != e.value.id
    }
  }).map((option: Option) => option.value))
}


</script>

<template lang="pug">
tr
  th {{ formLabel }}
  td
    MultiSelect(
      mode="tags"
      :value="selectedCompanies"
      @select="selectCompany"
      @deselect="deselectCompany"
      @clear="onClearCompany"
      :options="autocomplete"
      class="games__modal-basic-multiselect uk-border-pill"
      :placeholder="t('placeholder')"
      :searchable="true"
      :createOption="true"
      :filter-results="false"
      :resolve-on-load="false"
      :object="true"
      track-by="label"
      :delay="300"
      :noResultsText="t('no_results')"
      :noOptionsText="t('no_options')"
      :id="`game_${category}_company_ids`"
    )
</template>

<i18n lang="json">{
  "ja": {
    "developer": "デベロッパー",
    "placeholder": "会社名を入力",
    "no_results": "会社が見つかりませんでした",
    "no_options": "会社名を入力してください"
  }
}</i18n>

<style src="@vueform/multiselect/themes/default.css"></style>
